/**
 * Security Ongoing Job Hook using React Query
 *
 * Fetches the current ongoing security scan job (if any) for the given org/user.
 * Used app-wide to restore progress after page refresh and to know when to
 * prevent starting a new scan.
 *
 * Call this from a component mounted for the whole app (e.g. MainLayout) so
 * it runs as soon as the user lands in the app, not only on the Security Scanner page.
 */

import { useQuery } from '@tanstack/react-query';
import { getOngoingSecurityScanJob } from '@/services/security-scanner';

/**
 * Query key factory for security ongoing job
 */
export const securityOngoingJobKeys = {
  all: ['securityOngoingJob'] as const,
  detail: (orgId: string | number, userId: string | number) =>
    [...securityOngoingJobKeys.all, 'detail', orgId, userId] as const,
};

export interface UseSecurityOngoingJobParams {
  org_id?: number | string;
  user_id?: number | string;
}

/**
 * Hook to fetch the ongoing security scan job (if any).
 * Enable only when org_id and user_id are present (e.g. when user is in app with an org selected).
 *
 * @example
 * // In a component mounted app-wide (e.g. MainLayout):
 * const { data: ongoingJob } = useSecurityOngoingJob({
 *   org_id: selectedOrg?.id,
 *   user_id: user?.id,
 * });
 * // ongoingJob?.job_id when there is an ongoing scan
 */
export function useSecurityOngoingJob(params?: UseSecurityOngoingJobParams) {
  return useQuery({
    queryKey: securityOngoingJobKeys.detail(
      params?.org_id ?? '',
      params?.user_id ?? ''
    ),
    queryFn: () =>
      getOngoingSecurityScanJob({
        org_id: params!.org_id!,
        user_id: params!.user_id!,
      }),
    enabled: !!params?.org_id && !!params?.user_id,
    // Rehydration is a page-load operation — SSE handles live progress after that.
    // Tab-focus refetch from any route adds unnecessary API calls with no UX benefit.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
